import React, { useState } from 'react';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { FINANCIAL_IMPACT_OPTIONS, SOX_CONTROL_OPTIONS } from '../../constants/complianceOptions';

interface ComplianceQuestionnaireFormProps {
  jobId: string;
  onSubmitSuccess: (isSox: boolean) => void;
}

const ComplianceQuestionnaireForm: React.FC<ComplianceQuestionnaireFormProps> = ({
  jobId,
  onSubmitSuccess,
}) => {
  const [financialImpact, setFinancialImpact] = useState<string>('');
  const [soxControls, setSoxControls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleControlToggle = (value: string) => {
    if (value === 'NONE') {
      setSoxControls(soxControls.includes('NONE') ? [] : ['NONE']);
      return;
    }
    const filtered = soxControls.filter(c => c !== 'NONE');
    if (filtered.includes(value)) {
      setSoxControls(filtered.filter(c => c !== value));
    } else {
      setSoxControls([...filtered, value]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!financialImpact) {
      setError('Please select an annual financial impact option.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await makeApiCall(API_PATHS.COMPLIANCE.SUBMIT, 'POST', {
        jobId,
        annualFinancialImpact: financialImpact,
        soxControlTypes: soxControls.length > 0 ? soxControls : undefined,
      });
      onSubmitSuccess(response.isSox);
    } catch (err: any) {
      setError(err.message || 'Failed to submit compliance questionnaire.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="compliance-questionnaire" style={{ padding: '20px', border: '1px solid #d5dbdb', borderRadius: '8px', backgroundColor: '#fafafa', marginBottom: '16px' }}>
      <h3 style={{ marginTop: 0 }}>SOX Compliance Questionnaire</h3>
      <p style={{ color: '#545b64', fontSize: '14px' }}>
        This questionnaire is required before publishing to Andes or enabling schedules.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Question 1 */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
            Question 1: Annual Financial Impact <span style={{ color: '#d13212' }}>*</span>
          </label>
          <p style={{ color: '#545b64', fontSize: '13px', marginBottom: '8px' }}>
            What is the estimated annual financial impact of transactions or data processed through this dataset?
          </p>
          {FINANCIAL_IMPACT_OPTIONS.map(option => (
            <label key={option.value} style={{ display: 'block', marginBottom: '6px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="financialImpact"
                value={option.value}
                checked={financialImpact === option.value}
                onChange={() => setFinancialImpact(option.value)}
                style={{ marginRight: '8px' }}
              />
              {option.label}
            </label>
          ))}
        </div>

        {/* Question 2 */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
            Question 2: SOX Control Support (Optional)
          </label>
          <p style={{ color: '#545b64', fontSize: '13px', marginBottom: '8px' }}>
            Does this dataset support any SOX controls or financial reporting processes? Select all that apply.
          </p>
          {SOX_CONTROL_OPTIONS.map(option => (
            <label key={option.value} style={{ display: 'block', marginBottom: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={soxControls.includes(option.value)}
                onChange={() => handleControlToggle(option.value)}
                style={{ marginRight: '8px' }}
              />
              {option.label}
            </label>
          ))}
        </div>

        {error && (
          <div style={{ color: '#d13212', marginBottom: '12px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !financialImpact}
          style={{
            padding: '8px 20px',
            backgroundColor: isSubmitting || !financialImpact ? '#aab7b8' : '#0073bb',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: isSubmitting || !financialImpact ? 'not-allowed' : 'pointer',
            fontSize: '14px',
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Questionnaire'}
        </button>
      </form>
    </div>
  );
};

export default ComplianceQuestionnaireForm;
